import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Upload, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import QuestionCategoryForm from './QuestionCategoryForm';
import { leitnerService } from '../../services/leitnerService';
import InlineMathField from '../math/InlineMathField';
import CategoryFilter from './CategoryFilter';
import { MathRenderer } from '../math/MathRenderer';

const CreatorView = ({ bookId, addQuestion, questions, deleteQuestion, updateQuestion }) => {
  const [createType, setCreateType] = useState('simple');
  const [error, setError] = useState(null);
  
  // Simple question state
  const [simpleQuestionText, setSimpleQuestionText] = useState('');
  const [simpleOptions, setSimpleOptions] = useState([
    { id: 'opt_1', text: '' },
    { id: 'opt_2', text: '' }
  ]);
  const [simpleCorrectIds, setSimpleCorrectIds] = useState([]);
  
  // Stem question state
  const [stemText, setStemText] = useState('');
  const [stemImage, setStemImage] = useState(null);
  const [subQuestions, setSubQuestions] = useState([
    {
      text: '',
      options: [
        { id: 'opt_1', text: '' },
        { id: 'opt_2', text: '' }
      ],
      correctOptionIds: []
    }
  ]);
  
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  const [questionCategories, setQuestionCategories] = useState({ 
    subject: null, 
    grade: null, 
    topics: [] 
  });
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const resetSimpleForm = () => {
    setSimpleQuestionText('');
    setSimpleOptions([{ id: 'opt_1', text: '' }, { id: 'opt_2', text: '' }]);
    setSimpleCorrectIds([]);
    setError(null);
  };

  const resetStemForm = () => {
    setStemText('');
    setStemImage(null);
    setSubQuestions([{
      text: '',
      options: [{ id: 'opt_1', text: '' }, { id: 'opt_2', text: '' }],
      correctOptionIds: []
    }]);
    setError(null);
  };

  useEffect(() => {
    const filtered = leitnerService.filterQuestionsByCategories(questions, filters);
    setFilteredQuestions(filtered);
  }, [questions, filters]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await leitnerService.getQuestionCategories(bookId);
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    if (bookId) {
      loadCategories();
    }
  }, [bookId]);

  const handleSimpleSubmit = async (e) => {
    e.preventDefault();
    
    if (!simpleQuestionText.trim()) {
      setError('Question text is required');
      return;
    }
    
    const filledOptions = simpleOptions.filter(opt => opt.text.trim());
    if (filledOptions.length < 2) {
      setError('At least 2 options are required');
      return;
    }
    
    if (simpleCorrectIds.length === 0) {
      setError('At least one correct answer must be selected');
      return;
    }

    try {
      const questionData = {
        type: 'simple',
        subject: questionCategories.subject,
        grade: questionCategories.grade,
        topics: questionCategories.topics,
        text: simpleQuestionText.trim(),
        options: filledOptions,
        correctOptionIds: simpleCorrectIds
      };

      if (editingQuestion) {
        await updateQuestion(editingQuestion.id, questionData);
        setEditingQuestion(null);
      } else {
        await addQuestion(questionData);
      }

      resetSimpleForm();
    } catch (err) {
      setError('Failed to save question');
      console.error(err);
    }
  };

  const handleStemSubmit = async (e) => {
    e.preventDefault();
    
    if (!stemText.trim()) {
      setError('Stem text is required');
      return;
    }

    const validSubQuestions = subQuestions.filter(sq => sq.text.trim());
    if (validSubQuestions.length === 0) {
      setError('At least one sub-question is required');
      return;
    }

    for (let i = 0; i < validSubQuestions.length; i++) {
      const sq = validSubQuestions[i];
      const filledOptions = sq.options.filter(opt => opt.text.trim());
      
      if (filledOptions.length < 2) {
        setError(`Sub-question ${i + 1} needs at least 2 options`);
        return;
      }
      
      if (sq.correctOptionIds.length === 0) {
        setError(`Sub-question ${i + 1} needs at least one correct answer`);
        return;
      }
    }

    try {
      const processedSubQuestions = validSubQuestions.map(sq => ({
        id: `sq_${Date.now()}_${Math.random()}`,
        text: sq.text.trim(),
        options: sq.options.filter(opt => opt.text.trim()),
        correctOptionIds: sq.correctOptionIds
      }));

      const questionData = {
        type: 'stem',
        stemText: stemText.trim(),
        stemImage: stemImage,
        subject: questionCategories.subject,
        grade: questionCategories.grade,
        topics: questionCategories.topics,
        subQuestions: processedSubQuestions
      };

      if (editingQuestion) {
        await updateQuestion(editingQuestion.id, questionData);
        setEditingQuestion(null);
      } else {
        await addQuestion(questionData);
      }

      resetStemForm();
    } catch (err) {
      setError('Failed to save question');
      console.error(err);
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setQuestionCategories({ subject: null, grade: null, topics: [] });
    resetSimpleForm();
    resetStemForm();
  };

  const handleStartEdit = (question) => {
    setEditingQuestion(question);
    setError(null);
    
    // Prefill categories
    setQuestionCategories({
      subject: question.subject || null,
      grade: question.grade || null,
      topics: question.topics || []
    });
    
    document.getElementById('creator-form-container')?.scrollIntoView({ behavior: 'smooth' });
    
    if (question.type === 'simple') {
      setCreateType('simple');
      setSimpleQuestionText(question.text);
      setSimpleOptions(JSON.parse(JSON.stringify(question.options)));
      setSimpleCorrectIds([...question.correctOptionIds]);
    } else {
      setCreateType('stem');
      setStemText(question.stemText);
      setStemImage(question.stemImage || null);
      const subsWithoutIds = JSON.parse(JSON.stringify(question.subQuestions)).map(({ id, ...rest }) => rest);
      setSubQuestions(subsWithoutIds);
    }
  };

  const handleAddOption = (setter) => {
    setter(prev => prev.length < 6 ? [...prev, { id: `opt_${Date.now()}`, text: '' }] : prev);
  };

  const handleRemoveOption = (id, optionsSetter, correctIdSetter) => {
    optionsSetter(prev => prev.length > 2 ? prev.filter(opt => opt.id !== id) : prev);
    correctIdSetter(prev => prev.filter(correctId => correctId !== id));
  };

  const handleOptionTextChange = (id, text, setter) => {
    setter(prev => prev.map(opt => (opt.id === id ? { ...opt, text } : opt)));
  };

  const handleCorrectOptionToggle = (id, setter) => {
    setter(prev => prev.includes(id) ? prev.filter(correctId => correctId !== id) : [...prev, id]);
  };

  const moveOption = (index, direction, setter) => {
    setter(prev => {
      if ((direction === 'up' && index === 0) || (direction === 'down' && index === prev.length - 1)) return prev;
      const newOptions = [...prev];
      const item = newOptions[index];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      newOptions[index] = newOptions[swapIndex];
      newOptions[swapIndex] = item;
      return newOptions;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setStemImage(file);
    }
  };

  const handleRemoveImage = () => {
    setStemImage(null);
  };

  const addSubQuestion = () => {
    if (subQuestions.length >= 10) return;
    setSubQuestions(prev => [...prev, {
      text: '',
      options: [{ id: `opt_${Date.now()}_1`, text: '' }, { id: `opt_${Date.now()}_2`, text: '' }],
      correctOptionIds: []
    }]);
  };

  const removeSubQuestion = (index) => {
    if (subQuestions.length <= 1) return;
    setSubQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const updateSubQuestionText = (index, text) => {
    setSubQuestions(prev => prev.map((sq, i) => i === index ? { ...sq, text } : sq));
  };

  const updateSubQuestionOptions = (index, newOptions) => {
    setSubQuestions(prev => prev.map((sq, i) => i === index ? { ...sq, options: newOptions } : sq));
  };

  const updateSubQuestionCorrectIds = (index, newCorrectIds) => {
    setSubQuestions(prev => prev.map((sq, i) => i === index ? { ...sq, correctOptionIds: newCorrectIds } : sq));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div id="creator-form-container" className="bg-slate-800 p-6 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-indigo-400">
          {editingQuestion ? 'Edit Question' : 'Create New Question'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded-md text-red-200">
            {error}
          </div>
        )}

        {editingQuestion && (
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500 rounded-md flex justify-between items-center">
            <p className="text-blue-200 text-sm">
              Editing: {editingQuestion.type === 'simple' 
                ? `"${editingQuestion.text.substring(0, 30)}..."` 
                : `Stem: "${editingQuestion.stemText.substring(0, 30)}..."`}
            </p>
            <button 
              onClick={handleCancelEdit} 
              className="text-sm text-slate-300 hover:text-white font-semibold flex items-center"
            >
              &times; <span className="ml-1">Cancel</span>
            </button>
          </div>
        )}

        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setCreateType('simple')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition ${
              createType === 'simple' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Simple Question
          </button>
          <button
            onClick={() => setCreateType('stem')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition ${
              createType === 'stem' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Stem Question
          </button>
        </div>

        <QuestionCategoryForm
          value={questionCategories}
          onChange={setQuestionCategories}
          availableCategories={categories}
        />

        {createType === 'simple' ? (
          <form onSubmit={handleSimpleSubmit} className="space-y-6" id="creator-form">
            <div>
              <label htmlFor="question-text" className="block text-sm font-medium text-slate-300 mb-2">
                Question Text
              </label>
              <InlineMathField
               value={simpleQuestionText}
                onChange={setSimpleQuestionText}
                rows={3}
                className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                placeholder="Enter question text... (Ctrl+M for math)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Options</label>
              {simpleOptions.map((option, index) => (
                <div key={option.id} className="flex items-center space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => moveOption(index, 'up', setSimpleOptions)}
                    disabled={index === 0}
                    className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveOption(index, 'down', setSimpleOptions)}
                    disabled={index === simpleOptions.length - 1}
                    className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDown size={16} />
                  </button>
                  <div className="flex-grow">
                    <InlineMathField
                      value={option.text}
                      onChange={(text) => handleOptionTextChange(option.id, text, setSimpleOptions)}
                      rows={1}
                      className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                      placeholder={`Option ${index + 1} (Ctrl+M for math)`}
                    />
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={simpleCorrectIds.includes(option.id)}
                      onChange={() => handleCorrectOptionToggle(option.id, setSimpleCorrectIds)}
                      className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-green-500 focus:ring-green-500"
                    />
                    <span className="text-xs text-slate-400">Correct</span>
                  </label>
                  {simpleOptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(option.id, setSimpleOptions, setSimpleCorrectIds)}
                      className="p-1 text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              {simpleOptions.length < 6 && (
                <button
                  type="button"
                  onClick={() => handleAddOption(setSimpleOptions)}
                  className="mt-2 text-sm text-indigo-400 hover:text-indigo-300 flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Add Option
                </button>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition"
            >
              {editingQuestion ? 'Update Question' : 'Add Question'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStemSubmit} className="space-y-6" id="creator-form">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stem Text (Shared Prompt)
              </label>
              <InlineMathField
                value={stemText}
                onChange={setStemText}
                rows={3}
                className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                placeholder="Enter the shared stem/prompt for all sub-questions... (Ctrl+M for math)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stem Image (Optional)
              </label>
              {stemImage ? (
                <div className="relative inline-block">
                  <img
                    src={stemImage instanceof File ? URL.createObjectURL(stemImage) : stemImage}
                    alt="Stem"
                    className="max-h-40 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 p-1 bg-red-600 hover:bg-red-700 rounded-full text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-indigo-500 transition">
                  <Upload size={20} className="mr-2 text-slate-400" />
                  <span className="text-slate-400">Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">Sub-Questions</label>
              {subQuestions.map((sq, sqIndex) => (
                <div key={sqIndex} className="mb-6 p-4 bg-slate-700/50 rounded-md border border-slate-600">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-slate-200">Sub-question {sqIndex + 1}</h4>
                    {subQuestions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSubQuestion(sqIndex)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <InlineMathField
                    value={sq.text}
                    onChange={(text) => updateSubQuestionText(sqIndex, text)}
                    rows={2}
                    className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                  />

                  <div className="space-y-2">
                    {sq.options.map((option, optIndex) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            const newOpts = [...sq.options];
                            if (optIndex > 0) {
                              [newOpts[optIndex], newOpts[optIndex - 1]] = [newOpts[optIndex - 1], newOpts[optIndex]];
                              updateSubQuestionOptions(sqIndex, newOpts);
                            }
                          }}
                          disabled={optIndex === 0}
                          className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newOpts = [...sq.options];
                            if (optIndex < newOpts.length - 1) {
                              [newOpts[optIndex], newOpts[optIndex + 1]] = [newOpts[optIndex + 1], newOpts[optIndex]];
                              updateSubQuestionOptions(sqIndex, newOpts);
                            }
                          }}
                          disabled={optIndex === sq.options.length - 1}
                          className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <div className="flex-grow tex-sm bg-slate-700">
                          <InlineMathField
                            value={option.text}
                            onChange={(text) => {
                              const newOpts = [...sq.options];
                              newOpts[optIndex].text = text;
                              updateSubQuestionOptions(sqIndex, newOpts);
                            }}
                            rows={1}
                            className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 mb-3"
                            placeholder={`Option ${optIndex + 1} (Ctrl+M for math)`}
                          />
                        </div>
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sq.correctOptionIds.includes(option.id)}
                            onChange={() => {
                              const newCorrectIds = sq.correctOptionIds.includes(option.id)
                                ? sq.correctOptionIds.filter(id => id !== option.id)
                                : [...sq.correctOptionIds, option.id];
                              updateSubQuestionCorrectIds(sqIndex, newCorrectIds);
                            }}
                            className="h-4 w-4 rounded bg-slate-600 border-slate-500 text-green-500"
                          />
                          <span className="text-xs text-slate-400">✓</span>
                        </label>
                        {sq.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newOpts = sq.options.filter(o => o.id !== option.id);
                              updateSubQuestionOptions(sqIndex, newOpts);
                              const newCorrectIds = sq.correctOptionIds.filter(id => id !== option.id);
                              updateSubQuestionCorrectIds(sqIndex, newCorrectIds);
                            }}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {sq.options.length < 6 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newOpts = [...sq.options, { id: `opt_${Date.now()}`, text: '' }];
                          updateSubQuestionOptions(sqIndex, newOpts);
                        }}
                        className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center mt-2"
                      >
                        <Plus size={14} className="mr-1" /> Add Option
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {subQuestions.length < 10 && (
                <button
                  type="button"
                  onClick={addSubQuestion}
                  className="mt-3 text-sm text-indigo-400 hover:text-indigo-300 flex items-center"
                >
                  <Plus size={16} className="mr-1" /> Add Sub-Question
                </button>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition"
            >
              {editingQuestion ? 'Update Stem Question' : 'Add Stem Question'}
            </button>
          </form>
        )}
      </div>

      <div id="question-bank" className="bg-slate-800 p-6 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-indigo-400">Question Bank</h2>
        
        {/* Category Filter */}
        <div className="mb-6">
          <CategoryFilter
            categories={categories}
            selectedFilters={filters}
            onFilterChange={setFilters}
            onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
            questions={questions}
          />
        </div>

        <div className="space-y-4 max-h-[100vh] overflow-y-auto pr-2">
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-12 bg-slate-700 rounded-lg border border-slate-600">
              <p className="text-slate-400">
                {filters.subject || filters.grade || filters.topics?.length > 0 
                  ? 'No questions match the selected filters' 
                  : 'No questions created yet.'}
              </p>
            </div>
          ) : (
            [...filteredQuestions].reverse().map(q => (
              <div key={q.id} className="bg-slate-700 p-4 rounded-md">
                <div className="flex justify-between items-start">
                  <div className="flex-1 pr-4">
                    {q.type === 'simple' ? (
                      <div className="font-semibold text-slate-200 prose prose-sm prose-invert max-w-none">
                        <MathRenderer content={q.text} enableMarkdown={true} enableMath={true} />
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 flex-shrink-0 justify-end">
                          <button 
                            onClick={() => handleStartEdit(q)} 
                            className="text-sm text-indigo-400 hover:text-indigo-300 font-semibold"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => deleteQuestion(q.id)} 
                            className="text-sm text-red-500 hover:text-red-400 font-semibold"
                          >
                            &times; Delete
                          </button>
                        </div>
                        <div className="text-slate-200 flex items-baseline">
                          <span className="text-xs font-bold uppercase text-indigo-400 mr-2 not-prose">[STEM]</span>
                          <div className="prose prose-sm prose-invert max-w-none">
                            <MathRenderer content={q.stemText} enableMarkdown={true} enableMath={true} />
                          </div>
                        </div>
                        <div className="flex justify-center">
                        {q.stemImage && (
                          <img src={q.stemImage} alt="Stem reference" className="my-2 rounded-md max-h-48" />
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {q.type === 'simple' && (
                  <ol className="mt-2 text-sm list-decimal list-inside">
                    {q.options.map(opt => (
                      <li 
                        key={opt.id} 
                        className={`${q.correctOptionIds.includes(opt.id) ? 'text-green-400 font-medium' : 'text-slate-400'}`}
                      >
                        <div className="prose prose-sm prose-invert max-w-none inline-block">
                          <MathRenderer content={opt.text} enableMarkdown={true} enableMath={true} />
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                
                {q.type === 'stem' && (
                  <div className="mt-3 space-y-3 pl-4 border-l-2 border-slate-600">
                    {q.subQuestions.map((sq, idx) => (
                      <div key={sq.id}>
                        <div className="text-sm text-slate-300 prose prose-sm prose-invert max-w-none flex">
                          <span className="mr-2 not-prose">{idx + 1}.</span>
                          <MathRenderer content={sq.text} enableMarkdown={false} enableMath={true} />
                        </div>
                        <ol className="mt-1 text-xs list-disc list-inside pl-4">
                          {sq.options.map(opt => (
                            <li 
                              key={opt.id} 
                              className={`${sq.correctOptionIds.includes(opt.id) ? 'text-green-500' : 'text-slate-400'}`}
                            >
                              <div className="prose prose-xs prose-invert max-w-none inline-block">
                                <MathRenderer content={opt.text} enableMarkdown={false} enableMath={true} />
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                )}

                {/* Category Tags */}
                {(q.subject || q.grade || q.topics?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-slate-600 flex flex-wrap gap-2">
                    {q.subject && (
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                        {q.subject}
                      </span>
                    )}
                    {q.grade && (
                      <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                        {q.grade}
                      </span>
                    )}
                    {q.topics?.map(topic => (
                      <span key={topic} className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorView;